import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, TextField, FormControl, Alert, Typography } from "@mui/material";
import { IComment, INVITATION_RESPONSE_TYPE } from "../../../../../store/services/invitations/dto/invitations.dto";
import { LANG_AR, LANG_EN, RTL, LTR } from "../../constants";
import langEN from "../../lang.EN.json";
import langAR from "../../lang.AR.json";

import { LangType } from "../../../../../store/services/invitations/dto/invitations.dto";
import { useFormik } from "formik";
import { notifyError } from "../../../../../utils/NotifyError";
import { getErrorMessage, IError } from "../../../../../utils/getErrorMessage";
import ReCAPTCHA from "react-google-recaptcha";
import { commentSchema } from "../../../../../Pages/Invitations/schemas";
import {
	useAddCommentMutation,
	useRetrieveResponseMutation,
	useUnDeleteResponseMutation,
	useUpdateCommentMutation
} from "../../../../../store/services/invitations/invitations.service";
import { getOrCreateAnonId, setAnonId } from "../../../../../utils/getOrCreateAnonId";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import { RateReview as RateReviewIcon } from "@mui/icons-material";
import PassCodeModal from "../../../../Modals/PassCodeModal";
import { useLazyGetDeviceSubscriptionQuery } from "../../../../../store/services/notifications/notifications.service";
import { TemplateColorObject } from "../../../../../InvitationColorMatrix";
import ButtonComponent from "../../../../ButtonComponent";

interface ICommentInput {
	name: string;
	email: string;
	comment: string;
	response: INVITATION_RESPONSE_TYPE.COMING | INVITATION_RESPONSE_TYPE.CANT_COME;
	emailOTP?: string;
}

const CommentsInput = ({
	disable,
	language,
	invitationId,
	myComment,
	refetchComment,
	postComment,
	colorObject
}: {
	disable: boolean;
	language: LangType;
	invitationId: string;
	myComment: IComment | undefined;
	refetchComment: () => void;
	postComment: (shouldSubscribeToNotifications: boolean) => void;
	colorObject?: TemplateColorObject;
}) => {
	const anonymous_token = getOrCreateAnonId(invitationId);
	const [searchParams] = useSearchParams();
	const password = searchParams.get("password");
	const recaptchaRef = useRef<ReCAPTCHA>(null);
	const lang = language === LANG_EN ? langEN : langAR;
	const [callCommentMutation, { isLoading }] = useAddCommentMutation();
	const [requestRetrieveComment, { isLoading: isRetrievingComment }] = useRetrieveResponseMutation();
	const [requestUnDeleteComment, { isLoading: isLoadingUnDeleteComment }] = useUnDeleteResponseMutation();
	const [callUpdateCommentMutation, { isLoading: isUpdatingComment }] = useUpdateCommentMutation();
	const [callFetchSub, { data: deviceSub, isLoading: isFetchingSub }] = useLazyGetDeviceSubscriptionQuery();
	const [openVerifyEmail, setOpenVerifyEmail] = useState(false);
	const [alreadyRespondedBanner, setAlreadyRespondedBanner] = useState<boolean>(false);
	const [alreadyDeletedBanner, setAlreadyDeletedBanner] = useState<boolean>(false);
	const [isLoadingPassCodeModal, setIsLoadingPassCodeModal] = useState(false);
	const [shouldRetrieveResponse, setShouldRetrieveResponse] = useState<boolean>(false);
	const [shouldUnDeleteResponse, setShouldUnDeleteResponse] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	useEffect(() => {
		return () => {
			if (recaptchaRef.current) recaptchaRef.current.reset();
		};
	}, []);

	const initVals: ICommentInput = useMemo(() => {
		if (myComment) {
			const { name, email, comment, response, verify_email_otp: emailOTP } = myComment;
			return {
				name,
				email,
				comment,
				response,
				emailOTP: emailOTP ? emailOTP.toString() : undefined
			};
		}
		return {
			name: "",
			email: "",
			comment: "",
			response: INVITATION_RESPONSE_TYPE.COMING,
			emailOTP: undefined
		} as ICommentInput;
	}, [myComment]);

	useEffect(() => {
		if (initVals) {
			setValues(initVals);
		}
	}, [initVals]);

	const handleOnSubmit = async (values: ICommentInput) => {
		if (disable) {
			return;
		}
		if (!recaptchaRef.current) {
			return notifyError({ error: "Recaptcha failed" } as IError);
		}
		setIsSubmitting(true);
		setAlreadyRespondedBanner(false);
		setAlreadyDeletedBanner(false);
		let recaptchaToken: string | null = "";
		try {
			recaptchaToken = await recaptchaRef.current.executeAsync();
			console.log("TOKEN", recaptchaToken);
		} catch (e) {
			console.error(e);
		}

		if (myComment) {
			return callUpdateCommentMutation({
				...values,
				email: values.email.toLowerCase(),
				_id: invitationId,
				anonymous_token,
				recaptchaToken: recaptchaToken || "",
				password: password as string
			}).then(({ data, error }) => {
				if (data) {
					resetForm({ values });
					// toast.success("Response updated successfully! 🎉", { autoClose: 3000 });
					postComment(!deviceSub);
					setOpenVerifyEmail(false);
				}
				if (error) {
					if ((error as IError).status === 406) {
						const msg = getErrorMessage(error as IError);
						if (msg === "Already responded") {
							setIsSubmitting(false);
							return setAlreadyRespondedBanner(true);
						}
						if (msg === "Comment has been deleted") {
							setIsSubmitting(false);
							return setAlreadyDeletedBanner(true);
						}
						return setOpenVerifyEmail(true); // send OTP
					}
					notifyError(error as IError);
				}
				setIsSubmitting(false);
				setIsLoadingPassCodeModal(false);
			});
		}
		callCommentMutation({
			...values,
			email: values.email.toLowerCase(),
			_id: invitationId,
			anonymous_token,
			recaptchaToken: recaptchaToken || "",
			password: password as string
		}).then(({ data, error }) => {
			if (data) {
				resetForm({ values });
				// toast.success("Response posted successfully! 🎉", { autoClose: 3000 });
				postComment(!deviceSub);
				setOpenVerifyEmail(false);
			}
			if (error) {
				if ((error as IError).status === 406) {
					const msg = getErrorMessage(error as IError);
					if (msg === "Already responded") {
						setIsSubmitting(false);
						return setAlreadyRespondedBanner(true);
					}
					if (msg === "Comment has been deleted") {
						setIsSubmitting(false);
						return setAlreadyDeletedBanner(true);
					}
					return setOpenVerifyEmail(true); // send OTP
				}
				notifyError(error as IError);
			}
			setIsSubmitting(false);
			setIsLoadingPassCodeModal(false);
		});
	};

	const {
		handleSubmit,
		handleChange,
		values: { name, email, comment, response },
		dirty,
		isValid,
		setValues,
		resetForm
	} = useFormik({
		validationSchema: commentSchema,
		initialValues: initVals,
		onSubmit: handleOnSubmit
	});

	useEffect(() => {
		callFetchSub({ id: invitationId, email, anonymous_token }).then(({ data, error }) => {
			if (data) {
				console.log(data);
			}
			if (error) {
				console.log("error", error);
			}
		});
	}, [invitationId]);

	const handleVerifyEmail = (password: string[]) => {
		if (shouldRetrieveResponse) {
			setIsLoadingPassCodeModal(true);
			return handleRetrieveResponse({ emailOTP: password.join("") });
		}
		if (shouldUnDeleteResponse) {
			setIsLoadingPassCodeModal(true);
			return handleRetrieveResponse({ emailOTP: password.join(""), isDeleted: true });
		}
		let body: ICommentInput = { name, email, comment, response, emailOTP: password.join("") };
		setIsLoadingPassCodeModal(true);
		handleOnSubmit(body);
	};
	const handleVerify = () => {
		if (isUpdatingComment || isLoading || isSubmitting) return;
		handleOnSubmit({ name, email, comment, response });
	};

	const handleRetrieveResponse = async ({ emailOTP, isDeleted }: { emailOTP?: string; isDeleted?: boolean }) => {
		if (!recaptchaRef.current) {
			return notifyError({ error: "Recaptcha failed" } as IError);
		}
		setIsSubmitting(true);
		let recaptchaToken: string | null = "";
		try {
			recaptchaToken = await recaptchaRef.current.executeAsync();
			console.log("TOKEN", recaptchaToken);
		} catch (e) {
			console.error(e);
		}

		if (isDeleted) {
			return requestUnDeleteComment({ _id: invitationId, email, recaptchaToken: recaptchaToken || "", emailOTP }).then(
				({ data, error }) => {
					if (data) {
						console.log(data.response);
						if (data.response) {
							setAnonId(invitationId, data.response.anonymous_token);
							setTimeout(() => {
								refetchComment();
							}, 200);
						}
						setIsLoadingPassCodeModal(false);
						setAlreadyRespondedBanner(false);
						setAlreadyDeletedBanner(false);
						setOpenVerifyEmail(false);
					}
					if (error) {
						if ((error as IError).status === 406) {
							setShouldUnDeleteResponse(true);
							return setOpenVerifyEmail(true);
						}
						notifyError(error as IError);
					}
					setIsSubmitting(false);
					setIsLoadingPassCodeModal(false);
				}
			);
		}
		requestRetrieveComment({ _id: invitationId, email, recaptchaToken: recaptchaToken || "", emailOTP }).then(
			({ data, error }) => {
				if (data) {
					console.log(data.response);
					if (data.response) {
						setAnonId(invitationId, data.response.anonymous_token);
						setTimeout(() => {
							refetchComment();
						}, 200);
					}
					toast.success("Response retrieved successfully! 🎉", { autoClose: 3000 });
					setIsLoadingPassCodeModal(false);
					setAlreadyRespondedBanner(false);
					setAlreadyDeletedBanner(false);
					setOpenVerifyEmail(false);
				}
				if (error) {
					if ((error as IError).status === 406) {
						setShouldRetrieveResponse(true);
						return setOpenVerifyEmail(true);
					}
					notifyError(error as IError);
				}
				setIsSubmitting(false);
				setIsLoadingPassCodeModal(false);
			}
		);
	};

	return (
		<form onSubmit={handleSubmit}>
			{(alreadyRespondedBanner || alreadyDeletedBanner) && (
				<Box mt={1}>
					<Alert severity="warning" style={{ cursor: "pointer" }}>
						<Box display={"flex"} flexWrap={"wrap"} gap={2}>
							<Typography>
								{alreadyRespondedBanner
									? "You have already responded to this invitation from another device. "
									: "You previously deleted your response to this invitation "}
							</Typography>
							<ButtonComponent
								disabled={isSubmitting || isRetrievingComment || isLoadingUnDeleteComment}
								text={alreadyDeletedBanner ? "Repost" : "Retrieve"}
								loading={isSubmitting || isRetrievingComment || isLoadingUnDeleteComment}
								onClick={() => handleRetrieveResponse({ isDeleted: alreadyDeletedBanner })}
								variant={"contained"}
								style={{ marginLeft: "auto" }}
							/>
						</Box>
					</Alert>
				</Box>
			)}
			{myComment && !myComment?.is_email_verified && (
				<Box mt={1} display={"flex"} flexDirection={"column"} gap={1}>
					<Alert severity="warning" style={{ cursor: "pointer" }}>
						<Box display={"flex"} flexWrap={"wrap"} gap={2}>
							<Typography>Email not verified. Click “RESEND” button to resend verification.</Typography>
							<ButtonComponent
								onClick={handleVerify}
								variant={"contained"}
								style={{ marginLeft: "auto" }}
								disabled={isUpdatingComment || isLoading}
								loading={isUpdatingComment || isLoading}
								text={"Resend"}
							/>
						</Box>
					</Alert>
					{!myComment.isApproved && (
						<Alert severity="warning" style={{ cursor: "pointer" }}>
							<Box display={"flex"} flexWrap={"wrap"} gap={2}>
								<Typography>This response is hidden until your email is verified.</Typography>
							</Box>
						</Alert>
					)}
				</Box>
			)}
			<Box className={"comment_frame_content_wrapper"}>
				<Box className={"inputs_wrapper"}>
					<FormLabel id="name" className={language === LANG_AR ? RTL : ""}>
						{lang.form.name}
					</FormLabel>
					<TextField
						aria-labelledby={"name"}
						placeholder={"Enter your first and last name"}
						value={name}
						name={"name"}
						disabled={disable}
						onChange={handleChange}
						slotProps={{ htmlInput: { maxLength: 20, minLength: 6 }, input: { dir: language === LANG_AR ? RTL : LTR } }}
					/>
					<FormLabel id="email" className={language === LANG_AR ? RTL : ""}>
						{lang.form.email}
					</FormLabel>
					<TextField
						aria-labelledby={"email"}
						placeholder={"Eg: info@viteslink.com"}
						value={email}
						name={"email"}
						disabled={disable}
						onChange={handleChange}
						slotProps={{ input: { dir: language === LANG_AR ? RTL : LTR } }}
					/>
					<FormControl>
						<FormLabel id="response" className={language === LANG_AR ? RTL : ""}>
							{lang.form.attending}
						</FormLabel>
						<RadioGroup
							row
							aria-labelledby="response"
							name="response"
							value={response}
							onChange={handleChange}
							className={language === LANG_AR ? RTL : ""}
						>
							<FormControlLabel
								disabled={disable}
								value={INVITATION_RESPONSE_TYPE.COMING}
								control={<Radio />}
								label={lang.coming}
							/>
							<FormControlLabel
								disabled={disable}
								value={INVITATION_RESPONSE_TYPE.CANT_COME}
								control={<Radio />}
								label={lang.cant_come}
							/>
						</RadioGroup>
					</FormControl>

					<FormLabel id="comment" className={language === LANG_AR ? RTL : ""}>
						{lang.form.comment}
					</FormLabel>
					<TextField
						aria-labelledby={"comment"}
						placeholder={"Enter your comment"}
						value={comment}
						multiline
						minRows={3}
						disabled={disable}
						maxRows={5}
						name={"comment"}
						slotProps={{
							htmlInput: { maxLength: 180 },
							input: { dir: language === LANG_AR ? RTL : LTR }
						}}
						onChange={handleChange}
					/>
				</Box>
				<ButtonComponent
					variant={"contained"}
					className={`send_btn ${language === LANG_AR ? RTL : ""}`}
					fullWidth
					type="submit"
					disabled={disable || !dirty || !isValid || isLoading || isSubmitting || isUpdatingComment || openVerifyEmail}
					text={
						<>
							<Box mx={1} display={"flex"} justifyContent={"center"}>
								<RateReviewIcon />
							</Box>
							{myComment ? (myComment.is_email_verified ? lang.update : lang.send) : lang.send}
						</>
					}
					style={
						colorObject
							? { background: colorObject.addTemplateButton.background, color: colorObject.addTemplateButton.text }
							: {}
					}
					loading={isLoading || isSubmitting || isUpdatingComment}
				/>

				<ReCAPTCHA
					sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
					size="invisible"
					ref={recaptchaRef}
					badge="bottomright"
				/>
			</Box>
			{openVerifyEmail && (
				<PassCodeModal
					title={"Verify Email"}
					description={"An OTP has been sent to your email. Please enter it to continue."}
					open={true}
					onClose={() => {
						setOpenVerifyEmail(false);
						setIsSubmitting(false);
					}}
					onSave={handleVerifyEmail}
					loading={isLoading || isUpdatingComment || isLoadingPassCodeModal}
				/>
			)}
		</form>
	);
};

export default CommentsInput;
